import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Zap, Loader2, ExternalLink, Copy, MessageSquare, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";
import { copyTextAsync } from "../../utils/clipboard";
import { TapToPayGlyph, ttpLabel } from "../payments/TapToPayButton";

const ChargeStripeDialog = ({ open, onClose, doc, isDark, headers, onSendSms, onSendEmail, onTapToPay }) => {
  const [choice, setChoice] = useState("balance");
  const [customAmount, setCustomAmount] = useState("");
  const [creating, setCreating] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");

  const total = doc?.total || 0;
  const paid = doc?.amount_paid || 0;
  const balance = Math.max(total - paid, 0);
  const depositRequired = doc?.deposit_required || 0;
  const depositDue = Math.max(depositRequired - paid, 0);

  useEffect(() => {
    if (open) {
      setChoice(depositDue > 0 ? "deposit" : "balance");
      setCustomAmount("");
      setCheckoutUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.invoice_id]);

  if (!doc) return null;

  const amount = choice === "deposit"
    ? Math.min(depositDue, balance)
    : choice === "custom"
      ? (parseFloat(customAmount) || 0)
      : balance;

  const options = [
    ...(depositDue > 0 ? [{ key: "deposit", label: "Deposit due", amount: Math.min(depositDue, balance) }] : []),
    { key: "balance", label: paid > 0 ? "Remaining balance" : "Full amount", amount: balance },
    { key: "custom", label: "Custom amount", amount: null },
  ];

  const createCheckout = async () => {
    if (amount <= 0) { toast.error("Enter an amount greater than zero"); return; }
    if (amount > balance + 0.005) { toast.error(`Amount exceeds the remaining balance ($${balance.toFixed(2)})`); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/stripe/connect/create-checkout`, {
        invoice_id: doc.invoice_id,
        origin_url: window.location.origin,
        amount,
        payment_kind: choice,
      }, { headers });
      setCheckoutUrl(res.data.checkout_url || "");
      toast.success("Payment link created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create payment link");
    } finally { setCreating(false); }
  };

  const copyLink = async () => {
    const ok = await copyTextAsync(checkoutUrl);
    if (ok) toast.success("Link copied");
    else toast.error("Could not copy — long-press the link to copy manually");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="charge-stripe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#635BFF]" /> Charge via Stripe
          </DialogTitle>
          <DialogDescription>
            {doc.invoice_number} · {doc.customer_name} · Balance ${balance.toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        {!checkoutUrl ? (
          <>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What should the customer pay?</p>
              {options.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setChoice(o.key)}
                  className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-all ${
                    choice === o.key
                      ? "border-[#635BFF] bg-[#635BFF]/10"
                      : isDark ? "border-zinc-700 hover:border-zinc-500" : "border-gray-200 hover:border-gray-400"
                  }`}
                  data-testid={`charge-option-${o.key}`}
                >
                  <span className="text-sm font-medium text-foreground">{o.label}</span>
                  {o.amount !== null ? (
                    <span className="text-sm font-bold text-foreground">${o.amount.toFixed(2)}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">enter below</span>
                  )}
                </button>
              ))}
              {choice === "custom" && (
                <Input
                  type="number" step="0.01" min="0"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={`Up to $${balance.toFixed(2)}`}
                  className="h-10 text-base font-semibold"
                  data-testid="charge-custom-amount"
                />
              )}
              {amount > 0 && amount < balance - 0.005 && (
                <p className="text-[11px] text-amber-500">
                  Partial payment — the invoice will be marked <b>Partially Paid</b> (${Math.max(balance - amount, 0).toFixed(2)} will remain)
                </p>
              )}
            </div>

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
              <Button type="button" variant="ghost" onClick={onClose} data-testid="charge-cancel">Cancel</Button>
              <Button
                onClick={createCheckout}
                disabled={creating || amount <= 0}
                className="bg-[#635BFF] text-white hover:bg-[#5349E0] gap-1.5"
                data-testid="charge-create-link"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Create ${amount > 0 ? amount.toFixed(2) : ""} Payment Link
              </Button>
            </DialogFooter>

            {/* Tap to Pay — in-person contactless charge (native app, Stripe Connect active) */}
            {onTapToPay && (
              <button
                type="button"
                onClick={() => {
                  if (amount <= 0) { toast.error("Enter an amount greater than zero"); return; }
                  if (amount > balance + 0.005) { toast.error(`Amount exceeds the remaining balance ($${balance.toFixed(2)})`); return; }
                  onClose();
                  onTapToPay(doc, amount);
                }}
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed p-3 text-sm font-semibold text-foreground transition-colors border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10"
                data-testid="charge-tap-to-pay-btn"
              >
                <TapToPayGlyph className="w-4 h-4" />
                {ttpLabel()} — charge ${amount > 0 ? amount.toFixed(2) : "0.00"} in person
              </button>
            )}
          </>
        ) : (
          <div className="space-y-3" data-testid="charge-link-ready">
            <div className={`flex items-center gap-2 rounded-lg p-3 ${isDark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-200"}`}>
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <p className="text-sm text-foreground">Stripe payment link ready for <b>${amount.toFixed(2)}</b></p>
            </div>
            <p className={`text-[11px] break-all rounded-lg p-2 ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-gray-50 text-gray-500"}`}>{checkoutUrl}</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink} data-testid="charge-copy-link">
                <Copy className="w-3.5 h-3.5" /> Copy Link
              </Button>
              <Button size="sm" className="bg-[#635BFF] text-white hover:bg-[#5349E0] gap-1.5" onClick={() => window.open(checkoutUrl, "_blank")} data-testid="charge-open-link">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </Button>
            </div>
            {onSendSms && (
              <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => { onClose(); onSendSms(doc, { url: checkoutUrl, amount }); }} data-testid="charge-send-sms">
                <MessageSquare className="w-3.5 h-3.5" /> Text payment page to customer instead
              </Button>
            )}
            {onSendEmail && (
              <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => { onClose(); onSendEmail(doc, { url: checkoutUrl, amount }); }} data-testid="charge-send-email">
                <Mail className="w-3.5 h-3.5" /> Email payment link to customer
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChargeStripeDialog;
